import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Pagination,
  AppBar,
  Toolbar,
  Badge,
  CircularProgress,
  useTheme,
  useMediaQuery,
  styled,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import LeftToolbar from "./LeftToolbar";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import DescriptionIcon from "@mui/icons-material/Description";
import { alpha } from "@mui/material/styles";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import NotificationSnackbar from "./NotificationBar";
import jsPDF from "jspdf";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  const date = new Date(dateString);
  const pad = (num) => num.toString().padStart(2, "0");
  let hours = date.getUTCHours();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate()
  )} ${pad(hours)}:${pad(date.getUTCMinutes())} ${ampm}`;
};

const formatDateForLetter = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatTimeForLetter = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const MainContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f7fa",
});

const MainContent = styled(Box)(({ theme, $collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: $collapsed ? "80px" : "280px",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

function AppointmentRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    agentId: "",
    moduleId: "",
    appointmentReason: "",
    appointmentDate: "",
  });

  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [comment, setComment] = useState("");
  const [letterModalOpen, setLetterModalOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const [user, setUser] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      return userData ? JSON.parse(userData) : {};
    } catch (error) {
      console.error("Error parsing user data:", error);
      return {};
    }
  });

  const showNotification = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const loadAllData = async () => {
    setLoading(true);

    try {
      const [agentRes, categoryRes] = await Promise.all([
        axios.get("/api/support/supportAgents"),
        axios.get("/api/support/categories"),
      ]);

      const agentsData = agentRes.data || [];
      const categoriesData = Array.isArray(categoryRes.data)
        ? categoryRes.data
        : categoryRes.data && typeof categoryRes.data === "object"
        ? [categoryRes.data]
        : [];
      setAgents(agentsData);
      setCategories(categoriesData);
      const reqRes = await axios.get("/api/support/appointment-requests");
      const rawData = reqRes.data;
      const apiRequests = Array.isArray(rawData)
        ? rawData
        : Array.isArray(rawData?.data)
        ? rawData.data
        : [];
      const statusMap = {
        1: "Pending",
        2: "Verified",
        3: "Approved",
        4: "Rejected",
      };

      const formattedRequests = apiRequests.map((request, index) => ({
        ...request,
        id: request.id || request.appointmentId || index,
        moduleName: request.moduleName || "Unknown Module",
        agentName: request.supportAgent || "Unknown Agent",
        requestedByName:
          request.requestedByName || request.requestedBy || "Unknown",
        requestedOn:
          request.requestedOn || request.createdAt || new Date().toISOString(),
        status: statusMap[request.statusId] || "Unknown",
      }));

      setRequests(formattedRequests);
    } catch (error) {
      console.error("Error loading data:", error);
      showNotification("Failed to load appointment requests", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const generateAppointmentLetter = (request) => {
    const letterContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Appointment Letter Confirmation</title>
    <style>
        body {
            font-family: 'Arial', sans-serif;
            line-height: 1.6;
            margin: 0;
            padding: 20px;
            color: #333;
            background-color: #fff;
        }
        .letter-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding: 40px;
            border: 1px solid #ddd;
        }
        .header {
            text-align: center;
            margin-bottom: 40px;
            border-bottom: 3px solid #1565C0;
            padding-bottom: 20px;
        }
        .logo {
            font-size: 24px;
            font-weight: bold;
            color: #1565C0;
            margin-bottom: 10px;
        }
        .department {
            font-size: 16px;
            color: #666;
            margin-bottom: 5px;
        }
        .title {
            font-size: 20px;
            font-weight: bold;
            text-align: center;
            margin: 30px 0;
            text-transform: uppercase;
            color: #1565C0;
            text-decoration: underline;
        }
        .reference {
            text-align: right;
            margin-bottom: 30px;
        }
        .content {
            margin-bottom: 30px;
        }
        .details-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            background-color: #f8f9fa;
        }
        .details-table th,
        .details-table td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
        }
        .details-table th {
            background-color: #1565C0;
            color: white;
            font-weight: bold;
        }
        .details-table tr:nth-child(even) {
            background-color: #f9f9f9;
        }
        .signature-section {
            margin-top: 50px;
            display: flex;
            justify-content: space-between;
        }
        .signature-box {
            text-align: center;
            width: 200px;
        }
        .signature-line {
            border-top: 1px solid #333;
            margin-top: 50px;
            padding-top: 10px;
        }
        .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            font-size: 12px;
            color: #666;
        }
        @media print {
            body { margin: 0; }
            .letter-container { border: none; }
        }
    </style>
</head>
<body>
    <div class="letter-container">
        <div class="header">
            <div class="logo">IPPIS SUPPORT PORTAL</div>
        </div>

        <div class="reference">
            <strong>Ref No:</strong> IPPIS/SUPPORT/${
              request.id
            }/${new Date().getFullYear()}<br>
            <strong>Date:</strong> ${formatDateForLetter(new Date())}
        </div>

        <div class="title">Appointment Confirmation Letter</div>

        <div class="content">
            <p><strong>Dear ${request.requestedByName},</strong></p>
            
            <p>We are pleased to confirm your appointment request for technical support assistance,
            Your request has been reviewed and approved by ${
              request.agentName
            }.</p>

            <table class="details-table">
                <tr>
                    <th>Appointment Details</th>
                    <th>Information</th>
                </tr>
                <tr>
                    <td><strong>Appointment ID</strong></td>
                    <td>${request.id}</td>
                </tr>
                <tr>
                    <td><strong>Requested By</strong></td>
                    <td>${request.requestedByName}</td>
                </tr>
                <tr>
                    <td><strong>Request Date</strong></td>
                    <td>${formatDateForLetter(request.requestedOn)}</td>
                </tr>
                <tr>
                    <td><strong>Support Category/Module</strong></td>
                    <td>${request.moduleName}</td>
                </tr>
                <tr>
                    <td><strong>Approved By</strong></td>
                    <td>${request.agentName}</td>
                </tr>
                <tr>
                    <td><strong>Appointment Date</strong></td>
                    <td>${formatDateForLetter(request.appointmentDate)}</td>
                </tr>
                <tr>
                    <td><strong>Appointment Time</strong></td>
                    <td>${formatTimeForLetter(request.appointmentDate)}</td>
                </tr>
                <tr>
                    <td><strong>Reason for Appointment</strong></td>
                    <td>${request.appointmentReason}</td>
                </tr>
                <tr>
                    <td><strong>Status</strong></td>
                    <td><strong style="color: green;">APPROVED</strong></td>
                </tr>
            </table>
            
            <p>Thank you for using the IPPIS Support Portal.</p>
        </div>

        <div class="signature-section">
            <div class="signature-box">
            </div>
            <div class="signature-box">
            </div>
        </div>

        <div class="footer">
        </div>
    </div>
</body>
</html>`;

    return letterContent;
  };

  const handleDownloadLetter = (request) => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Add header image if available
    try {
      const headerImage = require("./assets/headerImage.png");
      doc.addImage(headerImage, "PNG", 15, 10, 180, 30);
    } catch (error) {
      console.error("Error loading header image:", error);
      doc.setFontSize(20);
      doc.setTextColor(21, 101, 192);
      doc.text("IPPIS SUPPORT PORTAL", 105, 25, { align: "center" });
      doc.setFontSize(14);
      doc.setTextColor(102, 102, 102);
      doc.text("Office of the Accountant General of the Federation", 105, 32, {
        align: "center",
      });
    }

    // Set initial position after header
    let yPosition = 50;

    // Add reference and date (FIXED TEMPLATE LITERALS)
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(
      `Ref No: IPPIS/SUPPORT/${request.id}/${new Date().getFullYear()}`,
      160,
      yPosition,
      { align: "right" }
    );
    doc.text(`Date: ${formatDateForLetter(new Date())}`, 160, yPosition + 5, {
      align: "right",
    });

    // Add title
    yPosition += 20;
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text("APPOINTMENT CONFIRMATION LETTER", 105, yPosition, {
      align: "center",
    });

    // Add content
    yPosition += 20;
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Dear ${request.requestedByName},`, 15, yPosition);

    yPosition += 10;
    const content = `We are pleased to confirm your appointment request for technical support assistance. Your request has been reviewed and approved by ${request.agentName}.`;
    const splitContent = doc.splitTextToSize(content, 180);
    doc.text(splitContent, 15, yPosition);

    yPosition += splitContent.length * 6 + 10;

    // Add appointment details table (simplified for PDF)
    const details = [
      ["Appointment ID:", request.id],
      ["Requested By:", request.requestedByName],
      ["Request Date:", formatDateForLetter(request.requestedOn)],
      ["Support Category/Module:", request.moduleName],
      ["Approved By:", request.agentName],
      ["Appointment Date:", formatDateForLetter(request.appointmentDate)],
      ["Appointment Time:", formatTimeForLetter(request.appointmentDate)],
      ["Reason for Appointment:", request.appointmentReason],
      ["Status:", "APPROVED"],
    ];

    details.forEach(([label, value]) => {
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(label, 15, yPosition);
      const valueText = doc.splitTextToSize(value.toString(), 120);
      doc.text(valueText, 70, yPosition);
      yPosition += Math.max(valueText.length * 5, 8);
    });

    // Add footer
    yPosition += 20;
    doc.text("Thank you for using the IPPIS Support Portal.", 15, yPosition);

    // Save the PDF
    doc.save(`Appointment_Letter_${request.id}.pdf`);
    showNotification("Appointment letter downloaded successfully", "success");
  };

  const handleViewLetter = (request) => {
    setSelectedRequest(request);
    setLetterModalOpen(true);
  };

  const handleCreateRequest = async () => {
    const { agentId, moduleId, appointmentReason, appointmentDate } = formData;

    if (!agentId || !moduleId || !appointmentReason || !appointmentDate) {
      return showNotification("All fields are required", "error");
    }

    const userToken = localStorage.getItem("userToken");
    if (!userToken) {
      return showNotification("Unauthorized. Please login first.", "error");
    }

    try {
      setLoading(true);

      const response = await fetch("/api/support/request-appointment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: userToken,
        },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to submit request");
      }
      await loadAllData();
      showNotification(
        result.message || "Request submitted successfully",
        "success"
      );
      setFormData({
        agentId: "",
        moduleId: "",
        appointmentReason: "",
        appointmentDate: "",
      });
      setCreateModalOpen(false);
    } catch (error) {
      console.error("Error creating request:", error);
      showNotification(error.message || "Error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleOpenModal = (request) => {
    setSelectedRequest(request);
    setViewModalOpen(true);
  };
  console.log("Selected Request:", selectedRequest);

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedRequest(null);
  };

  const handleActionClick = (type, request) => {
    setSelectedRequest(request); // Store the entire request object
    setActionType(type);
    setActionModalOpen(true);
  };

  const handleActionModalClose = () => {
    setActionModalOpen(false);
    setComment("");
    setActionType("");
  };

  const handleSubmitAction = async (id) => {
    try {
      setLoading(true);
      const response = await axios.put(
        "/api/support/appointment-request", // Matches backend route
        {
          id: id, // Use the passed id directly
          statusId: actionType === "accept" ? 3 : 4,
          comment: comment,
        }
      );

      const newStatus = actionType === "accept" ? "Approved" : "Rejected";
      const newStatusId = actionType === "accept" ? 3 : 4;

      setRequests((prev) =>
        prev.map((req) =>
          req.id === id
            ? {
                ...req,
                statusId: newStatusId,
                status: newStatus,
                comment: comment,
              }
            : req
        )
      );

      showNotification(
        `Appointment ${
          actionType === "accept" ? "approved" : "rejected"
        } successfully`,
        "success"
      );

      handleActionModalClose();
      handleCloseViewModal();

      if (actionType === "accept") {
        setTimeout(() => {
          if (window.confirm("Generate appointment letter now?")) {
            const updatedRequest = requests.find((req) => req.id === id);
            handleViewLetter({
              ...updatedRequest,
              status: "Approved",
              statusId: 3,
            });
          }
        }, 1000);
      }
    } catch (error) {
      console.error("Error updating appointment:", error);
      showNotification(
        error.response?.data?.message || `Failed to ${actionType} appointment`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const searchLower = searchTerm.toLowerCase();
  const filteredRequests = requests.filter((request) => {
    const searchFields = [
      request.requestedByName,
      request.moduleName,
      request.appointmentReason,
      request.agentName,
      request.status,
    ].map((field) => (field ? field.toString().toLowerCase() : ""));

    return searchFields.some((field) => field.includes(searchLower));
  });

  console.log("Filtered Requests:", filteredRequests);

  const getStatusChip = (status) => {
    const statusMap = {
      Pending: { color: "warning", label: "Pending" },
      Approved: { color: "success", label: "Approved" },
      Rejected: { color: "error", label: "Rejected" },
      Verified: { color: "info", label: "Verified" },
    };

    const statusData = statusMap[status] || {
      color: "default",
      label: status || "Unknown",
    };

    return (
      <Chip
        label={statusData.label}
        color={statusData.color}
        size="small"
        sx={{
          fontWeight: 600,
          textTransform: "capitalize",
        }}
      />
    );
  };

  return (
    <MainContainer>
      <AppBar
        position="fixed"
        sx={{
          backgroundImage: "linear-gradient(195deg, #0a2e52 0%, #1565C0 200%)",
          boxShadow:
            "0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 0, 0, 0.4)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          left: collapsed ? "80px" : "280px",
          width: `calc(100% - ${collapsed ? "80px" : "280px"})`,
          transition: theme.transitions.create(["width", "left"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          [theme.breakpoints.down("sm")]: {
            left: 0,
            width: "100%",
          },
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
            Appointment Requests
          </Typography>
          <IconButton color="inherit">
            <Badge badgeContent={requests.length} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <LeftToolbar onCollapse={setCollapsed} selectedMenu="Appointments" />

      <MainContent $collapsed={collapsed}>
        <Toolbar />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 2 : 0,
          }}
        >
          <TextField
            label="Search requests..."
            variant="outlined"
            size="small"
            fullWidth={isMobile}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: isMobile ? "100%" : 400,
              "& .MuiOutlinedInput-root": { borderRadius: "12px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreateModalOpen(true)}
            startIcon={<AddIcon />}
            size={isMobile ? "small" : "medium"}
          >
            {isMobile ? "Create" : "Create Request"}
          </Button>
        </Box>

        <Box sx={{ flex: 1, p: 2, bgcolor: "#f8f9fa", borderRadius: 2 }}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "16px",
              p: 3,
              mb: 3,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Appointment Requests
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredRequests.length}{" "}
                {filteredRequests.length === 1 ? "Request" : "Requests"} Found
              </Typography>
            </Box>

            <TableContainer
              component={Paper}
              sx={{ boxShadow: 2, borderRadius: "12px" }}
            >
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: theme.palette.primary.light,
                      "& th": {
                        color: "white",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      },
                    }}
                  >
                    {[
                      "ID",
                      "Appointment Reason",
                      "Module",
                      "Requested By",
                      "Status",
                      "Created On",
                      "Appointment Date",
                      "Actions",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        align="left"
                        sx={{
                          padding: theme.spacing(2),
                          borderBottom: `1px solid ${alpha(
                            theme.palette.divider,
                            0.1
                          )}`,
                          "&:first-of-type": {
                            borderLeft: `4px solid ${theme.palette.primary.main}`,
                          },
                          [theme.breakpoints.down("sm")]: {
                            padding: theme.spacing(1.5),
                          },
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : filteredRequests.length > 0 ? (
                    filteredRequests
                      .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                      .map((req) => (
                        <TableRow key={req.id}>
                          <TableCell>{req.id}</TableCell>
                          <TableCell>{req.appointmentReason}</TableCell>
                          <TableCell>{req.moduleName}</TableCell>
                          <TableCell>{req.requestedByName}</TableCell>
                          <TableCell>{getStatusChip(req.status)}</TableCell>
                          <TableCell>{formatDate(req.requestedOn)}</TableCell>
                          <TableCell>
                            {formatDate(req.appointmentDate)}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <IconButton onClick={() => handleOpenModal(req)}>
                                <VisibilityIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="textSecondary">
                          No appointment requests available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredRequests.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={Math.ceil(filteredRequests.length / rowsPerPage)}
                  page={page}
                  onChange={handleChangePage}
                  color="primary"
                  shape="rounded"
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    "& .MuiPaginationItem-root": { borderRadius: "999px" },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Create Request Dialog */}
        <Dialog
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.6)",
          }}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              background: "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 0,
              position: "relative",
              "&:before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 3,
                pb: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  background:
                    "linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                New Appointment Request
              </Typography>
              <IconButton
                onClick={() => setCreateModalOpen(false)}
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="agent-select-label">Support Staff</InputLabel>
              <Select
                labelId="agent-select-label"
                id="agent-select"
                name="agentId"
                value={formData.agentId}
                onChange={handleInputChange}
                label="Support Staff"
                sx={{
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                  },
                }}
              >
                {agents.length === 0 ? (
                  <MenuItem disabled>Loading agents...</MenuItem>
                ) : (
                  agents.map((agent) => (
                    <MenuItem
                      key={agent.id || agent.agentId}
                      value={agent.id || agent.agentId}
                    >
                      {agent.SupportAgent ||
                        agent.name ||
                        agent.fullName ||
                        `Agent ${agent.id || agent.agentId}`}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="category-select-label">
                Support Category
              </InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                name="moduleId"
                value={formData.moduleId}
                onChange={handleInputChange}
                label="Support Category"
              >
                {categories.length === 0 ? (
                  <MenuItem disabled>Loading categories...</MenuItem>
                ) : (
                  categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.categoryName ||
                        category.name ||
                        `Category ${category.id}`}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Appointment Date & Time"
              name="appointmentDate"
              type="datetime-local"
              value={formData.appointmentDate}
              onChange={handleInputChange}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EventIcon color="action" />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                min: new Date().toISOString().slice(0, 16),
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />

            <TextField
              label="Reason for Appointment"
              name="appointmentReason"
              fullWidth
              multiline
              rows={4}
              value={formData.appointmentReason}
              onChange={handleInputChange}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setCreateModalOpen(false)}
              disabled={loading}
              sx={{ borderRadius: "12px" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateRequest}
              disabled={
                loading ||
                !formData.agentId ||
                !formData.moduleId ||
                !formData.appointmentReason ||
                !formData.appointmentDate
              }
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
              }}
            >
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Appointment Modal */}
        <Dialog
          open={viewModalOpen}
          onClose={handleCloseViewModal}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          BackdropProps={{
  sx: {
    backgroundColor: "rgba(0, 0, 0, 0.6) !important",
  },
}}
        >
          <DialogTitle
            sx={{
              p: 0,
              position: "relative",
              "&:before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 3,
                pb: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  background:
                    "linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Appointment Details
              </Typography>
              <IconButton
                onClick={handleCloseViewModal}
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {selectedRequest && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 3,
                  "& .MuiTypography-root": {
                    mb: 2,
                    display: "flex",
                    flexDirection: "column",
                  },
                  "& strong": {
                    color: theme.palette.text.secondary,
                    fontSize: "0.875rem",
                    mb: 0.5,
                  },
                }}
              >
                <Box>
                  <Typography>
                    <strong>Requested By</strong>
                    {selectedRequest.requestedByName ||
                      selectedRequest.requestedBy ||
                      "Unknown"}
                  </Typography>

                  <Typography>
                    <strong>Appointment Reason</strong>
                    {selectedRequest.appointmentReason || "Unknown"}
                  </Typography>

                  <Typography>
                    <strong>Requested On</strong>
                    {formatDate(selectedRequest.requestedOn)}
                  </Typography>

                  <Typography>
                    <strong>Module</strong>
                    {selectedRequest.moduleName || "No Module"}
                  </Typography>

                  <Typography>
                    <strong>Support Agent</strong>
                    {selectedRequest.agentName || "Not assigned"}
                  </Typography>
                </Box>

                <Box>
                  <Typography>
                    <strong>Appointment Date</strong>
                    {formatDate(selectedRequest.appointmentDate)}
                  </Typography>
                  <Typography>
                    <strong>Comment</strong>
                    {selectedRequest.comment || "No comments"}
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              p: 3,
              justifyContent: "space-between",
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                <Typography
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <strong style={{ minWidth: "60px" }}>Status</strong>
                  <Box sx={{ display: "inline-flex" }}>
                    {getStatusChip(selectedRequest?.status || "Pending")}
                  </Box>
                </Typography>
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              {selectedRequest?.status === "Approved" && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleViewLetter(selectedRequest)}
                  startIcon={<DescriptionIcon />}
                  sx={{
                    borderRadius: "12px",
                    px: 3,
                    textTransform: "none",
                  }}
                >
                  Generate Letter
                </Button>
              )}
             {selectedRequest?.status === "Pending" && (
  <>
    {(() => {
       
      const normalizeName = (name) =>
        name
          ?.trim()
          .toLowerCase()
          .replace(/\s+/g, " ")
          .split(" ")
          .sort()
          .join(" ");

      const normalizedAgent = normalizeName(selectedRequest.supportAgent);
      const normalizedUser = normalizeName(
        `${user.firstName} ${user.lastName}`
      );

      console.log("=== DEBUG BUTTON DISABLE LOGS ===");
      console.log("Raw supportAgent:", selectedRequest.supportAgent);
      console.log("Raw user name:", `${user.firstName} ${user.lastName}`);
      console.log("Normalized supportAgent:", normalizedAgent);
      console.log("Normalized user:", normalizedUser);
      console.log(
        "Do they match?",
        normalizedAgent === normalizedUser
      );

      const isMatch = normalizedAgent === normalizedUser;

      return (
        <>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleActionClick("reject", selectedRequest)}
            disabled={!isMatch}
            sx={{
              borderRadius: "12px",
              px: 3,
              textTransform: "none",
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.08),
              },
            }}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleActionClick("accept", selectedRequest)}
            disabled={!isMatch}
            sx={{
              borderRadius: "12px",
              px: 3,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
              },
            }}
          >
            Accept
          </Button>
        </>
      );
    })()}
  </>
)}


            </Box>
          </DialogActions>
        </Dialog>

        {/* Accept/Reject Modal */}
        <Dialog
          open={actionModalOpen}
          onClose={handleActionModalClose}
          maxWidth="sm"
          fullWidth
          BackdropProps={{
  sx: {
    backgroundColor: "rgba(0, 0, 0, 0.9) !important",
  },
}}
          PaperProps={{
            sx: {
              borderRadius: "16px",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {actionType === "accept"
              ? "Accept Appointment"
              : "Reject Appointment"}
            <IconButton onClick={handleActionModalClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={`Add a comment (${
                actionType === "accept" ? "optional" : "required"
              })`}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              sx={{ mt: 2 }}
              required={actionType === "reject"}
            />
          </DialogContent>

          <DialogActions>
            {selectedRequest && (
              <>
                <Button
                  onClick={() => handleSubmitAction(selectedRequest.id)}
                  variant="contained"
                  color={actionType === "accept" ? "success" : "error"}
                  disabled={
                     
                    // Reject action still needs a comment
                    (actionType === "reject" && !comment)
                  }
                  sx={{ borderRadius: "12px" }}
                >
                  {actionType === "accept"
                    ? "Confirm Acceptance"
                    : "Confirm Rejection"}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Appointment Letter Modal */}
        <Dialog
          open={letterModalOpen}
          onClose={() => setLetterModalOpen(false)}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
          BackdropProps={{
  sx: {
    backgroundColor: "rgba(0, 0, 0, 0.6) !important",
  },
}}
        >
          <DialogTitle
            sx={{
              p: 0,
              position: "relative",
              "&:before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #1565C0 0%, #0a2e52 100%)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 3,
                pb: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  background:
                    "linear-gradient(90deg, #1565C0 0%, #0a2e52 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Appointment Letter
              </Typography>
              <IconButton
                onClick={() => setLetterModalOpen(false)}
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              p: 0,
              maxHeight: "70vh",
              overflow: "auto",
              bgcolor: "#f5f5f5",
            }}
          >
            {selectedRequest && (
              <Box
                sx={{
                  p: 3,
                  bgcolor: "white",
                  m: 2,
                  borderRadius: "8px",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                }}
                dangerouslySetInnerHTML={{
                  __html: generateAppointmentLetter(selectedRequest)
                    .replace("<!DOCTYPE html>", "")
                    .replace(/<html[^>]*>/, "")
                    .replace("</html>", "")
                    .replace(/<head[^>]*>[\s\S]*?<\/head>/, "")
                    .replace(/<body[^>]*>/, "")
                    .replace("</body>", "")
                    .replace(/<style[^>]*>[\s\S]*?<\/style>/, ""),
                }}
              />
            )}
          </DialogContent>
          <DialogActions
            sx={{
              p: 3,
              justifyContent: "space-between",
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: "#f8f9fa",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Appointment ID: {selectedRequest?.id} | Generated:{" "}
              {new Date().toLocaleDateString()}
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleDownloadLetter(selectedRequest)}
                startIcon={<DescriptionIcon />}
                sx={{
                  borderRadius: "12px",
                  px: 3,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                Download Letter
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        <NotificationSnackbar
          open={snackbarOpen}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
          severity={snackbarSeverity}
        />
      </MainContent>
    </MainContainer>
  );
}
export default AppointmentRequests;
